import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito directamente; todos los pagos son procesados de forma segura por nuestro proveedor de pagos.'
  }, {
    heading: 'Uso de la Información',
    text: 'Tu información personal se utiliza exclusivamente para: procesar y entregar tu compra, enviarte el producto digital adquirido, y comunicarnos contigo en caso de ser necesario respecto a tu pedido.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración, divulgación o destrucción.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio puede utilizar cookies para mejorar tu experiencia de navegación y analizar el tráfico del sitio. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar algunas funcionalidades.'
  }, {
    heading: 'Tus Derechos',
    text: 'Tienes derecho a acceder, rectificar o eliminar tu información personal en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los canales disponibles en nuestro sitio.'
  }, {
    heading: 'Cambios en esta Política',
    text: 'Nos reservamos el derecho de actualizar esta política de privacidad en cualquier momento. Los cambios serán publicados en esta página con la fecha de última actualización.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto Digital',
    text: 'Al realizar tu compra, adquieres una licencia personal e intransferible para acceder y utilizar el contenido digital "Controla la Ansiedad", que incluye la guía PDF, el audiolibro y la tarjeta S.O.S. Este contenido es para uso personal únicamente.'
  }, {
    heading: 'Entrega del Producto',
    text: 'El producto se entrega de forma digital e inmediata tras confirmar el pago. Recibirás un enlace de descarga en tu correo electrónico. Es tu responsabilidad proporcionar una dirección de correo válida.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido del producto está protegido por derechos de autor. Queda prohibida la reproducción, distribución, modificación o reventa del material sin autorización expresa por escrito.'
  }, {
    heading: 'Descargo de Responsabilidad Médica',
    text: 'Este producto tiene fines informativos y educativos. No constituye consejo médico, diagnóstico ni tratamiento profesional. Si experimentas una crisis severa de ansiedad, consulta a un profesional de la salud. Los resultados pueden variar de persona a persona.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Ofrecemos una garantía de satisfacción. Si no estás conforme con el producto, puedes solicitar un reembolso dentro del período establecido, sujeto a nuestras condiciones de devolución.'
  }, {
    heading: 'Limitación de Responsabilidad',
    text: 'No nos hacemos responsables por daños directos o indirectos derivados del uso o la imposibilidad de uso del producto. El uso del material es bajo tu propia responsabilidad.'
  }, {
    heading: 'Modificaciones',
    text: 'Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del producto tras cualquier cambio constituye la aceptación de los nuevos términos.'
  }]
};
function LegalModal({
  type,
  onClose
}: {
  type: 'privacy' | 'terms';
  onClose: () => void;
}) {
  const content = type === 'privacy' ? privacyContent : termsContent;
  return <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2
  }} data-id="element-54">
      {/* Overlay */}
      <div className="absolute inset-0 bg-brown/60 backdrop-blur-sm" onClick={onClose} data-id="element-55" />

      {/* Modal */}
      <motion.div className="relative bg-cream rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" initial={{
      scale: 0.95,
      y: 20
    }} animate={{
      scale: 1,
      y: 0
    }} exit={{
      scale: 0.95,
      y: 20
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} data-id="element-56">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-sage-light" data-id="element-57">
          <h2 className="text-2xl font-heading font-bold text-brown" data-id="element-58">
            {content.title}
          </h2>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-sage-light transition-colors text-brown-light" aria-label="Cerrar" data-id="element-59">
            <XIcon className="w-5 h-5" data-id="element-60" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-6 py-6 space-y-6" data-id="element-61">
          {content.sections.map((section, index) => <div key={index} data-id="element-62">
              <h3 className="text-lg font-heading font-semibold text-brown mb-2" data-id="element-63">
                {section.heading}
              </h3>
              <p className="text-brown-light leading-relaxed" data-id="element-64">{section.text}</p>
            </div>)}

          <p className="text-sm text-brown-light/60 pt-4 border-t border-sage-light" data-id="element-65">
            Última actualización:{' '}
            {new Date().toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>
      </motion.div>
    </motion.div>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brown text-cream/80 py-12 px-4 sm:px-6 lg:px-8 text-center" data-id="element-66">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-67">
          <p className="text-sm max-w-2xl mx-auto leading-relaxed opacity-70" data-id="element-68">
            Descargo de responsabilidad: Este producto tiene fines informativos
            y educativos. No reemplaza el diagnóstico, consejo o tratamiento
            médico profesional. Si estás experimentando una crisis severa, por
            favor contacta a un profesional de la salud o a una línea de
            emergencia en tu país.
          </p>

          <div className="h-px w-full max-w-md mx-auto bg-cream/10" data-id="element-69"></div>

          <div className="flex justify-center gap-6 text-sm" data-id="element-70">
            <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 hover:text-cream transition-colors" data-id="element-71">
              Política de Privacidad
            </button>
            <span className="opacity-30" data-id="element-72">|</span>
            <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 hover:text-cream transition-colors" data-id="element-73">
              Términos y Condiciones
            </button>
          </div>

          <p className="text-sm" data-id="element-74">
            &copy; {new Date().getFullYear()} Controla la Ansiedad. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-75">
        {activeModal && <LegalModal type={activeModal} onClose={() => setActiveModal(null)} data-id="element-76" />}
      </AnimatePresence>
    </>;
}